import React, {useCallback, useState} from 'react';
import {Alert, Button, Paper, Stack, Typography} from '@mui/material';
import {useReportStore} from '../../../../useReportStore';
import {scrollToBlockId} from '../../../../../../components/prosemirror-editor';
import {applyStatementReferenceMark} from '../../../../../../components/prosemirror-editor/helpers/applyStatementReferenceMark';

/**
 * Displays a list of statements that require citations
 */
const StatementsList = ({statements, onStatementsChange}) => {
    const editorView = useReportStore(s => s.editorView);

    const handleNavigate = useCallback((statement) => {
        if (!editorView || !statement?.blockId) return;

        const range = Array.isArray(statement?.range) && statement.range.length === 2
            ? statement.range
            : undefined;

        scrollToBlockId(editorView, statement.blockId, range ? {range} : undefined);
    }, [editorView]);

    const handleAccept = useCallback((statement, index) => {
        if (!editorView) return {ok: false, reason: 'Editor not available'};

        if (statement?.blockId) {
            scrollToBlockId(editorView, statement.blockId);
        }

        const result = applyStatementReferenceMark(editorView, {
            blockId: statement?.blockId,
            range: statement?.range,
            text: statement?.text,
        });

        if (result.ok) {
            const newStatements = statements.filter((_, i) => i !== index);
            onStatementsChange?.(newStatements);
        }

        return result;
    }, [editorView, statements, onStatementsChange]);

    const handleReject = useCallback((index) => {
        const newStatements = statements.filter((_, i) => i !== index);
        onStatementsChange?.(newStatements);
    }, [statements, onStatementsChange]);

    if (!statements || statements.length === 0) {
        return (
            <Stack p={2}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Click the button above to analyze your document.
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={2} p={2}>
            {statements.map((statement, index) => (
                <StatementCard
                    key={`${statement.blockId}-${index}`}
                    statement={statement}
                    index={index}
                    onNavigate={handleNavigate}
                    onAccept={handleAccept}
                    onReject={handleReject}
                />
            ))}
        </Stack>
    );
};

/**
 * Individual statement card
 */
const StatementCard = ({statement, index, onNavigate, onAccept, onReject}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const handleAccept = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        setError(null);
        try {
            const result = await onAccept?.(statement, index);
            if (result && !result.ok) {
                setError(result.reason || 'Could not apply reference mark');
            }
        } finally {
            setBusy(false);
        }
    }, [busy, onAccept, statement, index]);

    const handleReject = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        setError(null);
        onReject?.(index);
    }, [busy, onReject, index]);

    return (
        <Paper
            sx={{
                p: 2,
                cursor: 'pointer',
                '&:hover': {backgroundColor: 'action.hover'},
                transition: 'background-color 0.2s',
                opacity: busy ? 0.75 : 1,
            }}
            onClick={() => onNavigate?.(statement)}
        >
            <Stack spacing={1}>
                <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                    Statement requiring citation:
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        fontStyle: 'italic',
                        pl: 1,
                        borderLeft: 2,
                        borderColor: 'primary.main',
                    }}
                >
                    {statement?.text || 'No text available'}
                </Typography>
            </Stack>

            {error && (
                <Alert severity="warning" sx={{mt: 1.5, py: 0}} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{mt: 2}}>
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{textTransform: 'none'}}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleReject}
                    disabled={busy}
                >
                    Reject
                </Button>
                <Button
                    type="button"
                    size="small"
                    variant="contained"
                    sx={{textTransform: 'none'}}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleAccept}
                    disabled={busy}
                    title="Create reference mark for this statement"
                >
                    Accept
                </Button>
            </Stack>
        </Paper>
    );
};

export default StatementsList;
